import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { patchV1CompaniesCompanyIdTicketsId } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { noop } from 'lodash-es';
import { useCallback, useContext, useState } from 'react';

import ActionsColumn from '~components/Load/LoadsTableColumns/ActionsColumn';
import ApprovalActionsColumn from '~components/Load/LoadsTableColumns/ApprovalActionsColumn';
import CreatedColumn from '~components/Load/LoadsTableColumns/CreatedColumn';
import DatetimeColumn from '~components/Load/LoadsTableColumns/DatetimeColumn';
import DriverAndTruckColumn from '~components/Load/LoadsTableColumns/DriverAndTruckColumn';
import PreviewColumn from '~components/Load/LoadsTableColumns/PreviewColumn';
import QuantityAndMaterialColumn from '~components/Load/LoadsTableColumns/QuantityAndMaterialColumn';
import StatusColumn from '~components/Load/LoadsTableColumns/StatusColumn';
import WaypointsColumn from '~components/Load/LoadsTableColumns/WaypointsColumn';
import {
  ConfirmDeleteLoadAndTicketsDialog,
  useDeleteLoadAndTickets,
} from '~hooks/useDeleteLoadAndTickets/useDeleteLoadAndTickets';
import useEffectiveCompanyId from '~hooks/useEffectiveCompanyId/useEffectiveCompanyId';
import { Job } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { Order } from '~hooks/useOrders/models';
import { Ticket } from '~hooks/useTickets/models';
import { useTickets } from '~hooks/useTickets/useTickets';
import { TicketDrawerBusyReason } from '~pages/Approvals/Files/components/TicketDrawer/constants';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { flaggedLoadRowSx } from '~utils/loads/load-utils';
import { isViewerAccountCustomer } from '~utils/user/user-utils';

import LoadsTabQueryContext from '../LoadsTabQueryContext';
import LoadPhaseColumn from './LoadPhaseColumn';
import { columns, gridTemplateColumns } from './loads-tab-loads-table-columns';

interface TableRowProps {
  job?: Job;
  load: Load;
  loadNumber: number;
  onClick: (loadNumber: number, load: Load, ticket?: Ticket) => void;
  order: Order;
  ticket?: Ticket;
}

export default function TableRow({
  job,
  load,
  loadNumber,
  onClick,
  order,
  ticket,
}: TableRowProps) {
  const { toasterStore, userStore } = useStores();
  const companyId = useEffectiveCompanyId();
  const [busyReason, setBusyReason] = useState<TicketDrawerBusyReason>();

  const {
    idsOfBusyLoads,
    refreshLoads,
    removeStoredLoadById,
    removeStoredTicketsById,
    updateStoredLoad,
    updateStoredTicket,
  } = useContext(LoadsTabQueryContext);

  const {
    confirmationDialogState,
    deleteLoadOrAlertIfItHasAssociatedTickets,
    isDeleting: isDeletingLoad,
  } = useDeleteLoadAndTickets({
    onLoadDeleted: () => removeStoredLoadById(load.id),
    onTicketsDeleted: (idsOfDeletedTickets) => {
      removeStoredTicketsById(idsOfDeletedTickets);
    },
  });

  const { updateTicketPotentialDuplicate } = useTickets();

  const userPermissions = userStore.getPermissions();
  const isReadOnlyTicket = isViewerAccountCustomer(userStore, order.account);
  const isLoadDeletable = !isReadOnlyTicket && userPermissions.canDeleteLoad;
  const isBusyRow = isDeletingLoad || idsOfBusyLoads.includes(load.id);

  const handleTicketFlaggedStatusChange = useCallback(async () => {
    if (!ticket?.id) {
      return;
    }

    try {
      setBusyReason(TicketDrawerBusyReason.CHANGING_TICKET_FLAGGED_STATE);

      const response = await patchV1CompaniesCompanyIdTicketsId({
        path: { 'company-id': companyId, id: ticket.id },
        body: { flagged: !ticket.flagged },
      });
      const updatedTicket = Ticket.parse(response.data.data);

      updateStoredTicket(updatedTicket);
      toasterStore.push(alert(t('approvals.ticket_updated'), AlertTypes.success));
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.tickets.failed_to_update'));
    } finally {
      setBusyReason(undefined);
    }
  }, [companyId, ticket?.id, ticket?.flagged, updateStoredTicket]);

  const ticketId = ticket?.id;
  const handlePotentialDuplicateChange = useCallback(
    async (potentialDuplicate: boolean) => {
      if (!ticketId) {
        return;
      }

      const updatedTicket = await updateTicketPotentialDuplicate({
        companyId,
        id: ticketId,
        potentialDuplicate,
      });
      updateStoredTicket(updatedTicket);
      await refreshLoads();
    },
    [
      companyId,
      refreshLoads,
      ticketId,
      updateStoredTicket,
      updateTicketPotentialDuplicate,
    ],
  );

  const handleDeleteLoadClick = useCallback(() => {
    if (isLoadDeletable) {
      deleteLoadOrAlertIfItHasAssociatedTickets(ticket ? [ticket] : [], load.id);
    }
  }, [isLoadDeletable, load.id, ticket]);

  const handleRowClick = useCallback(() => {
    if (!isBusyRow) {
      onClick(loadNumber, load, ticket);
    }
  }, [isBusyRow, onClick, load, loadNumber, ticket]);

  return (
    <Box
      data-test-id="loads-tab-table-row"
      onClick={isBusyRow ? undefined : handleRowClick}
      sx={(theme) => ({
        borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        cursor: 'pointer',
        display: 'grid',
        gridAutoRows: 'auto',
        gridColumn: `span ${columns.length}`,
        gridTemplateColumns,
        overflow: 'hidden',
        ...(ticket?.isPotentialDuplicateFlagged && flaggedLoadRowSx(theme)),
        ...(isBusyRow
          ? {
              bgcolor: theme.brandV2.colors.treadGray10,
              borderBottomColor: theme.brandV2.colors.treadGray6,
              cursor: 'not-allowed',
              opacity: 0.5,
              pointerEvents: 'none',
            }
          : {}),
      })}
    >
      <DatetimeColumn ticket={ticket} sx={{ ...columns[0].sx }} />
      <CreatedColumn boldTime createdAt={ticket?.createdAt} sx={{ ...columns[1].sx }} />
      <StatusColumn load={load} sx={{ ...columns[2].sx }} />
      <PreviewColumn
        onPotentialDuplicateChange={
          userPermissions.canEditTicketPotentialDuplicate
            ? handlePotentialDuplicateChange
            : undefined
        }
        showExportIndicator
        sx={{ ...columns[3].sx }}
        ticket={ticket}
      />
      <DriverAndTruckColumn job={job} ticket={ticket} sx={{ ...columns[4].sx }} />
      <QuantityAndMaterialColumn
        job={job}
        load={load}
        ticket={ticket}
        sx={{ ...columns[5].sx }}
      />
      <LoadPhaseColumn load={load} sx={{ ...columns[6].sx }} />
      <WaypointsColumn load={load} sx={{ ...columns[7].sx }} />
      <ApprovalActionsColumn
        isOrderEditable={order.editable}
        jobId={job?.jobId}
        load={load}
        onLoadUpdate={updateStoredLoad}
        onAiReviewDismissed={() => void refreshLoads()}
        ticket={ticket}
        sx={{ ...columns[8].sx }}
      />
      <ActionsColumn
        busyReason={isDeletingLoad ? TicketDrawerBusyReason.DELETING_LOAD : busyReason}
        onDeleteLoadClick={isLoadDeletable ? handleDeleteLoadClick : undefined}
        onTicketFlaggedStatusChangeClick={handleTicketFlaggedStatusChange}
        ticket={ticket}
        sx={{ ...columns[9].sx }}
      />

      {load.notes && (
        <Box
          sx={(theme) => ({
            gridColumn: '1 / -2',
            py: 1,
            px: 1,
            mx: 1,
            mb: 1,
            backgroundColor: theme.brandV2.colors.treadGray8,
            borderRadius: theme.spacing(0.5),
          })}
        >
          <Typography
            variant="body2"
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray1,
              whiteSpace: 'pre-line',
            })}
          >
            {load.notes}
          </Typography>
        </Box>
      )}

      <ConfirmDeleteLoadAndTicketsDialog
        isOpen={confirmationDialogState.isOpen}
        onClose={confirmationDialogState.isOpen ? confirmationDialogState.onClose : noop}
        onConfirm={
          confirmationDialogState.isOpen
            ? confirmationDialogState.onConfirm
            : () => Promise.resolve()
        }
      />
    </Box>
  );
}
